import { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  makeStyles,
} from '@material-ui/core';
import classNames from 'classnames';
import CustomButton from '../customButton';
import VirtualizedList from '../virtualizedList';
import ActionButton from '../actionButton';
import VirtualItem from './virtualItem';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  dialogPaperWrapper: { display: 'block', position: 'relative' },
  paper: {
    boxSizing: 'border-box',
    borderRadius: 8,
    padding: spacing(8),
    margin: 0,
    minWidth: 534,
    maxWidth: 534,
    minHeight: 'auto',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%,-50%) ',
  },
  titleRoot: {
    marginBottom: spacing(4),
    padding: 0,
  },
  contentRoot: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: spacing(4),
    padding: 0,
    minHeight: 0,
    overflowY: 'initial',
  },
  itemsWrapper: {
    minHeight: 20,
    maxHeight: 136,
  },
  itemsViewport: {
    maxHeight: 136,
  },
  subtitleLevel: {
    marginTop: spacing(2),
  },
  actionsRoot: {
    boxSizing: 'border-box',
    borderTop: `1px solid ${primary.bluish7}`,
    padding: spacing(4, 0, 0),
    margin: 0,
  },
  title: {
    wordBreak: 'break-word',
    paddingRight: spacing(8),
  },
  closeButtonIcon: {
    position: 'absolute',
    right: 29,
    top: 28,
  },
  button: {
    backgroundColor: primary.blue1,
    border: 'none',
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '24px',
    padding: spacing(2, 4),
  },
  closeButton: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish2}`,
    color: primary.bluish2,
    '&:hover': {
      backgroundColor: primary.white,
    },
  },
  itemWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  item: {
    wordBreak: 'break-word',
  },
  subtitle: {
    marginBottom: spacing(2),
  },
  scrollY: {
    right: -19,
  },
  scroll: {
    backgroundColor: primary.bluish7,
  },
}));

const WarningDialog = ({
  translations,
  items = [],
  isOpened,
  isTagsDelete = false,
  isAssignedToLevels = false,
  hasForceDelete = false,
  onClose,
  onForceDelete = () => {},
}) => {
  const classes = useStyles();

  const getVirtualItemSize = useCallback(
    index => (index === items.length - 1 ? 20 : 24),
    [items.length]
  );

  const renderItem = useCallback(
    item => <VirtualItem item={item} isTag={isTagsDelete} />,
    [isTagsDelete]
  );

  return (
    <Dialog
      open={isOpened}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      classes={{
        scrollPaper: classes.dialogPaperWrapper,
        paper: classes.paper,
      }}
      onClose={onClose}
    >
      <DialogTitle
        id="alert-dialog-title"
        classes={{ root: classes.titleRoot }}
      >
        <Typography className={classes.title} variant="h5" component="div">
          {translations.title}
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.contentRoot }}>
        {isAssignedToLevels && (
          <Typography
            className={classNames(classes.subtitle, classes.subtitleLevel)}
            variant="subtitle1"
          >
            {translations.levelsSubtitle}
          </Typography>
        )}
        {!isArrayEmpty(items) && (
          <div className={classes.itemsWrapper}>
            <VirtualizedList
              viewportClass={classes.itemsViewport}
              verticalScrollClass={classes.scrollY}
              itemClass={classes.itemWrapper}
              items={items}
              count={items?.length ?? 0}
              gap={4}
              estimateSize={getVirtualItemSize}
              renderItem={renderItem}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions classes={{ root: classes.actionsRoot }}>
        <CustomButton
          className={classNames(classes.button, classes.closeButton)}
          type="withTextDarkRounded"
          onClick={onClose}
        >
          {translations.closeButton}
        </CustomButton>
        {hasForceDelete && (
          <CustomButton
            className={classes.button}
            type="withTextDarkRounded"
            onClick={() => {
              onForceDelete();
            }}
          >
            {translations.forceDeleteButton}
          </CustomButton>
        )}
      </DialogActions>
      <ActionButton
        className={classes.closeButtonIcon}
        type={ACTION_BUTTON_TYPES.CLOSE}
        onClickHandler={onClose}
      />
    </Dialog>
  );
};

WarningDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  hasForceDelete: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  isTagsDelete: PropTypes.bool,
  isAssignedToLevels: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onForceDelete: PropTypes.func,
};

export default WarningDialog;
